import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Search } from 'lucide-react';

import { authService, blogService } from '../services/api';
import BlogDetail from '../components/Blog/BlogDetail';
import SEO from '../components/SEO/SEO';
import { SITE_URL, SITE_NAME } from '../config/site';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    checkAuth();
    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await authService.verify();
        if (res.success) setIsAdmin(true);
      } catch (error) {
        authService.logout();
      }
    }
  };

  const fetchPost = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await blogService.getBySlug(slug);
      if (res.success) {
        setPost(res.data);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await blogService.delete(id);
      navigate('/blogs');
    } catch (error) {
      alert('Error deleting blog');
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center pt-16" />;
  }

  if (notFound || !post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 pt-16 px-6 text-center">
        <SEO title="Article Not Found" noIndex />
        <div className="w-12 h-12 rounded-full bg-brand-cream flex items-center justify-center text-brand-gold">
          <Search className="w-6 h-6" />
        </div>
        <h1 className="font-serif text-2xl text-brand-green-dark font-semibold">Article Not Found</h1>
        <p className="text-sm text-gray-400 max-w-xs font-light">
          This article may have been moved or removed.
        </p>
        <Link
          to="/blogs"
          className="mt-2 text-xs font-bold text-brand-green-dark hover:text-brand-gold uppercase tracking-wider border-b-2 border-brand-green-dark hover:border-brand-gold transition-all"
        >
          Back to Blog
        </Link>
      </div>
    );
  }

  const postUrl = `${SITE_URL}/blogs/${post.slug}`;
  const description = post.excerpt || post.subtitle || '';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description,
    image: post.coverImage ? [post.coverImage] : undefined,
    datePublished: post.createdAt,
    dateModified: post.updatedAt || post.createdAt,
    author: {
      '@type': 'Person',
      name: 'Amelia Downie',
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/amelia-logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
  };

  return (
    <div className="min-h-[80vh] bg-brand-cream-light font-sans text-gray-700 selection:bg-brand-gold/20 selection:text-brand-green-dark pb-16 pt-16">
      <SEO
        title={post.title}
        description={description}
        url={postUrl}
        image={post.coverImage || undefined}
        type="article"
        jsonLd={articleSchema}
      />
      <BlogDetail
        post={post}
        isAdmin={isAdmin}
        onBack={() => navigate('/blogs')}
        onEdit={(p) => navigate(`/blogs?edit=${p._id}`)}
        onDelete={handleDelete}
      />
    </div>
  );
}
